/**
 * Admin API Service
 * Handles all CRUD operations for the LSAS admin portal
 */

import { getAuthHeaders } from './auth';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || 'https://lonestaraerialsolutions.com/admin';

// Debug logging only in development
if (process.env.NODE_ENV === 'development') {
  console.log('Admin API Configuration:', {
    API_BASE_URL: API_BASE_URL,
    NODE_ENV: process.env.NODE_ENV
  });
}

/**
 * Base API request function with error handling
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} API response
 */
const apiRequest = async (endpoint, options = {}) => {
  try {
    const authHeaders = await getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        ...authHeaders,
        ...options.headers,
      },
      credentials: 'include', // Include cookies for Cloudflare Access
      ...options,
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};

// =============================================================================
// CLIENTS API
// =============================================================================

export const clientsApi = {
  /**
   * Get all clients
   * @returns {Promise<Array>} List of clients
   */
  getAll: async () => {
    return apiRequest('/api/clients');
  },

  /**
   * Get client by ID
   * @param {string} id - Client ID
   * @returns {Promise<Object>} Client data
   */
  getById: async (id) => {
    return apiRequest(`/api/clients/${id}`);
  },

  /**
   * Create new client
   * @param {Object} clientData - Client data
   * @returns {Promise<Object>} Created client
   */
  create: async (clientData) => {
    return apiRequest('/api/clients', {
      method: 'POST',
      body: JSON.stringify(clientData),
    });
  },

  /**
   * Update client
   * @param {string} id - Client ID
   * @param {Object} clientData - Updated client data
   * @returns {Promise<Object>} Updated client
   */
  update: async (id, clientData) => {
    return apiRequest(`/api/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(clientData),
    });
  },

  /**
   * Delete client
   * @param {string} id - Client ID
   * @returns {Promise<Object>} Deletion result
   */
  delete: async (id) => {
    return apiRequest(`/api/clients/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get client with related data (emails, projects)
   * @param {string} id - Client ID
   * @returns {Promise<Object>} Client with related data
   */
  getWithRelated: async (id) => {
    return apiRequest(`/api/clients/${id}/full`);
  },
};

// =============================================================================
// CLIENT EMAILS API
// =============================================================================

export const clientEmailsApi = {
  /**
   * Get all client emails
   * @returns {Promise<Array>} List of client emails
   */
  getAll: async () => {
    return apiRequest('/api/client-emails');
  },

  /**
   * Get client emails by client ID
   * @param {string} clientId - Client ID
   * @returns {Promise<Array>} List of client emails
   */
  getByClientId: async (clientId) => {
    return apiRequest(`/api/client-emails/client/${clientId}`);
  },

  /**
   * Create new client email
   * @param {Object} emailData - Email data
   * @returns {Promise<Object>} Created email
   */
  create: async (emailData) => {
    return apiRequest('/api/client-emails', {
      method: 'POST',
      body: JSON.stringify(emailData),
    });
  },

  /**
   * Update client email
   * @param {number} id - Email ID
   * @param {Object} emailData - Updated email data
   * @returns {Promise<Object>} Updated email
   */
  update: async (id, emailData) => {
    return apiRequest(`/api/client-emails/${id}`, {
      method: 'PUT',
      body: JSON.stringify(emailData),
    });
  },

  /**
   * Delete client email
   * @param {number} id - Email ID
   * @returns {Promise<Object>} Deletion result
   */
  delete: async (id) => {
    return apiRequest(`/api/client-emails/${id}`, {
      method: 'DELETE',
    });
  },
};

// Convenience functions for client emails
export const getClientEmails = clientEmailsApi.getAll;
export const getClientEmailsByClient = clientEmailsApi.getByClientId;
export const createClientEmail = clientEmailsApi.create;
export const updateClientEmail = clientEmailsApi.update;
export const deleteClientEmail = clientEmailsApi.delete;

// =============================================================================
// PROJECTS API
// =============================================================================

export const projectsApi = {
  /**
   * Get all projects
   * @returns {Promise<Array>} List of projects
   */
  getAll: async () => {
    return apiRequest('/api/projects');
  },

  /**
   * Get project by ID
   * @param {string} id - Project ID
   * @returns {Promise<Object>} Project data
   */
  getById: async (id) => {
    return apiRequest(`/api/projects/${id}`);
  },

  /**
   * Get projects by client ID
   * @param {string} clientId - Client ID
   * @returns {Promise<Array>} List of projects
   */
  getByClientId: async (clientId) => {
    return apiRequest(`/api/projects/client/${clientId}`);
  },

  /**
   * Create new project
   * @param {Object} projectData - Project data
   * @returns {Promise<Object>} Created project
   */
  create: async (projectData) => {
    return apiRequest('/api/projects', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  },

  /**
   * Update project
   * @param {string} id - Project ID
   * @param {Object} projectData - Updated project data
   * @returns {Promise<Object>} Updated project
   */
  update: async (id, projectData) => {
    return apiRequest(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(projectData),
    });
  },

  /**
   * Delete project
   * @param {string} id - Project ID
   * @returns {Promise<Object>} Deletion result
   */
  delete: async (id) => {
    return apiRequest(`/api/projects/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get project with assets
   * @param {string} id - Project ID
   * @returns {Promise<Object>} Project with assets
   */
  getWithAssets: async (id) => {
    return apiRequest(`/api/projects/${id}/full`);
  },
};


// =============================================================================
// ASSETS API
// =============================================================================

export const assetsApi = {
  /**
   * Get all assets
   * @returns {Promise<Array>} List of assets
   */
  getAll: async () => {
    return apiRequest('/api/assets');
  },

  /**
   * Get asset by ID
   * @param {string} id - Asset ID
   * @returns {Promise<Object>} Asset data
   */
  getById: async (id) => {
    return apiRequest(`/api/assets/${id}`);
  },

  /**
   * Get assets by project ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} List of assets
   */
  getByProjectId: async (projectId) => {
    return apiRequest(`/api/assets/project/${projectId}`);
  },

  /**
   * Create new asset
   * @param {Object} assetData - Asset data
   * @returns {Promise<Object>} Created asset
   */
  create: async (assetData) => {
    return apiRequest('/api/assets', {
      method: 'POST',
      body: JSON.stringify(assetData),
    });
  },

  /**
   * Update asset
   * @param {string} id - Asset ID
   * @param {Object} assetData - Updated asset data
   * @returns {Promise<Object>} Updated asset
   */
  update: async (id, assetData) => {
    return apiRequest(`/api/assets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(assetData),
    });
  },

  /**
   * Delete asset
   * @param {string} id - Asset ID
   * @returns {Promise<Object>} Deletion result
   */
  delete: async (id) => {
    return apiRequest(`/api/assets/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Generate admin download link for asset
   * @param {string} id - Asset ID
   * @returns {Promise<Object>} Download URL and metadata
   */
  getDownloadLink: async (id) => {
    return apiRequest(`/api/assets/${id}/download`);
  },

  /**
   * Initialize multipart upload
   * @param {Object} uploadData - Upload data (project_id, filename, file_size, content_type)
   * @returns {Promise<Object>} Upload initialization data
   */
  initializeUpload: async (uploadData) => {
    return apiRequest('/api/assets/upload-init', {
      method: 'POST',
      body: JSON.stringify(uploadData),
    });
  },

  /**
   * Complete multipart upload
   * @param {Object} completeData - Completion data (asset_id, r2_key, upload_id, parts, project_id, filename, content_type)
   * @returns {Promise<Object>} Asset data
   */
  completeUpload: async (completeData) => {
    return apiRequest('/api/assets/upload-complete', {
      method: 'POST',
      body: JSON.stringify(completeData),
    });
  },

  /**
   * Abort multipart upload
   * @param {Object} abortData - Abort data (r2_key, upload_id)
   * @returns {Promise<Object>} Abort confirmation
   */
  abortUpload: async (abortData) => {
    return apiRequest('/api/assets/upload-abort', {
      method: 'POST',
      body: JSON.stringify(abortData),
    });
  },

  /**
   * Get additional part URLs for multipart upload
   * @param {Object} partData - Part data (r2_key, upload_id, start_part, count)
   * @returns {Promise<Object>} Part URLs
   */
  getPartUrls: async (partData) => {
    return apiRequest('/api/assets/upload-part', {
      method: 'POST',
      body: JSON.stringify(partData),
    });
  },

};

// =============================================================================
// AUDIT LOG API
// =============================================================================

export const auditLogApi = {
  /**
   * Get audit logs with pagination
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Paginated audit logs
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/audit-log${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Get audit logs by actor email
   * @param {string} email - Actor email
   * @returns {Promise<Array>} List of audit logs
   */
  getByActor: async (email) => {
    return apiRequest(`/api/audit-log/actor/${encodeURIComponent(email)}`);
  },

  /**
   * Get unique actions for dropdown
   * @returns {Promise<Array>} List of unique actions
   */
  getActions: async () => {
    return apiRequest('/api/audit-log/actions');
  },

  /**
   * Get audit logs by action
   * @param {string} action - Action type
   * @returns {Promise<Array>} List of audit logs
   */
  getByAction: async (action) => {
    return apiRequest(`/api/audit-log/action/${action}`);
  },

  /**
   * Create audit log entry
   * @param {Object} data - Audit log data
   * @returns {Promise<Object>} Created audit log entry
   */
  create: async (data) => {
    return apiRequest('/api/audit-log', 'POST', data);
  },
};

// AUTH API
// =============================================================================

export const authApi = {
  /**
   * Log logout action and get redirect URL
   * @returns {Promise<Object>} Logout response with redirect URL
   */
  logout: async () => {
    return apiRequest('/api/auth/logout', 'POST');
  },
};

// =============================================================================
// DASHBOARD API
// =============================================================================

export const dashboardApi = {
  /**
   * Get dashboard statistics
   * @returns {Promise<Object>} Dashboard stats
   */
  getStats: async () => {
    return apiRequest('/api/dashboard/stats');
  },

  /**
   * Get recent activity
   * @param {number} limit - Number of recent activities
   * @returns {Promise<Array>} Recent activities
   */
  getRecentActivity: async (limit = 10) => {
    return apiRequest(`/api/dashboard/activity?limit=${limit}`);
  },
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Format file size in bytes to human readable format
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format date string to readable format in Central Time
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date in Central Time
 */
export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

/**
 * Generate unique ID for new records
 * @param {string} prefix - ID prefix
 * @returns {string} Unique ID
 */
export const generateId = (prefix = '') => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substr(2, 5);
  return prefix ? `${prefix}-${timestamp}-${random}` : `${timestamp}-${random}`;
};
